import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useDraftAutosave(body, { ready, enabled, initialId }) {
  const idRef = useRef(initialId || null);
  const baselineRef = useRef(null);
  const pendingRef = useRef(null);
  const inflightRef = useRef(null);
  const stoppedRef = useRef(false);
  const mountedRef = useRef(true);
  const [savedAt, setSavedAt] = useState(null);
  const json = JSON.stringify(body);

  const flush = useCallback(async () => {
    if (inflightRef.current) await inflightRef.current;
    const j = pendingRef.current;
    if (!j || stoppedRef.current) return;
    pendingRef.current = null;
    const b = JSON.parse(j);
    const req = idRef.current ? api.put(`/projects/${idRef.current}`, b) : api.post("/projects", b);
    inflightRef.current = req
      .then(({ data }) => {
        if (!idRef.current && mountedRef.current) window.history.replaceState(window.history.state, "", `/simulation/new?id=${data.id}`);
        idRef.current = data.id;
        baselineRef.current = j;
        setSavedAt(new Date());
      })
      .catch(() => { pendingRef.current = pendingRef.current || j; })
      .finally(() => { inflightRef.current = null; });
    await inflightRef.current;
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (baselineRef.current === null) {
      baselineRef.current = json;
      return;
    }
    if (!enabled || stoppedRef.current || json === baselineRef.current) return;
    pendingRef.current = json;
    const t = setTimeout(flush, 1500);
    return () => clearTimeout(t);
  }, [json, ready, enabled, flush]);

  useEffect(() => {
    const onHide = () => {
      const j = pendingRef.current;
      if (!j || stoppedRef.current) return;
      pendingRef.current = null;
      const id = idRef.current;
      fetch(id ? `${API}/projects/${id}` : `${API}/projects`, {
        method: id ? "PUT" : "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("cee_token")}` },
        body: j,
      }).catch(() => {});
    };
    mountedRef.current = true;
    window.addEventListener("pagehide", onHide);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("pagehide", onHide);
      flush();
    };
  }, [flush]);

  const finalize = useCallback(async () => {
    pendingRef.current = null;
    if (inflightRef.current) await inflightRef.current;
    stoppedRef.current = true;
    return idRef.current;
  }, []);

  const resume = useCallback(() => { stoppedRef.current = false; }, []);

  return { savedAt, finalize, resume };
}
